import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Card, Row, Col, Badge, Spinner, Alert, Container, Button, Accordion, Table, Modal } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaClock,
  FaBook,
  FaGraduationCap,
  FaSync,
  FaExclamationTriangle,
  FaArrowLeft,
  FaDownload,
  FaPrint,
  FaCalendarCheck,
  FaChartBar,
  FaListAlt,
  FaSchool,
  FaUserGraduate
} from 'react-icons/fa';
import moment from 'moment';
import { getCachedData, setCachedData } from '../utils/apiCache';
import { renderPdfToImages } from '../utils/pdfPrint';
import Swal from 'sweetalert2';
import Header from './Header';
import { Link } from 'react-router-dom';
import { all_routes } from './router/all_routes';
import { ProductContext } from "../context/AppContext";
import Select from "react-select";

const Timetable = () => {
  const [timetableData, setTimetableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [viewMode, setViewMode] = useState('seasons'); // 'seasons', 'days', 'periods', 'details'
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedTimeTableId, setSelectedTimeTableId] = useState(null);
  const [selectedTimeTableEntry, setSelectedTimeTableEntry] = useState(null);
  const [optionalTeachers, setOptionalTeachers] = useState([]);

  const routes = all_routes;
  const { GetstudentData } = useContext(ProductContext);
  const clsSecId = GetstudentData?.cls_sec.id;
  const sessionId = GetstudentData?.session;
  const className = GetstudentData?.cls_sec.cls?.Classes || "";
  const sectionName = GetstudentData?.cls_sec.section?.section || "";

  const [showModal, setShowModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  // Fetch seasons
  const fetchSeasons = async () => {
    if (!clsSecId || !sessionId) {
      setError('Class section ID or Session ID not found');
      return;
    }
    const cacheKey = `timetable_seasons_${clsSecId}_${sessionId}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      setSeasonOptions(cached);
      setLoading(false);

    }
    try {
      if (!cached) setLoading(true);
      setError(null);
      const authtoken = localStorage.getItem('token');
      const response = await fetch(
        `https://api.mirai.healthcare/api/timetable/manageschooltime`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'x-auth': authtoken
          }
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data && Array.isArray(data)) {
        const formattedOptions = data.map(item => ({
          value: item.id,
          label: item.season,
          seasonData: item
        }));
        setSeasonOptions(formattedOptions);
        setCachedData(cacheKey, formattedOptions);
      }
    } catch (err) {
      console.error('Error fetching seasons:', err);
      setError(err.message || 'Failed to fetch seasons');
    } finally {
      setLoading(false);
    }
  };

  const [showSeasonModal, setShowSeasonModal] = useState(false);
  const [showWeeklyModal, setShowWeeklyModal] = useState(false);
  const [showTimeTableModal, setShowTimeTableModal] = useState(false);
  const [showImage, setShowImage] = useState(false);


  const [timetableImage, setTimetableImage] = useState("");
  const [loadingImage, setLoadingImage] = useState(false);

  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedFileUrl, setSelectedFileUrl] = useState("");
  const [selectedFileTitle, setSelectedFileTitle] = useState("");
  const [zoomScale, setZoomScale] = useState(1);
  const [rotationDegrees, setRotationDegrees] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);

  const handleOpenFileModal = (url, title) => {
    setSelectedFileUrl(url);
    setSelectedFileTitle(title);
    setZoomScale(1);
    setRotationDegrees(0);
    setShowFileModal(true);
  };

  const printFile = async (url) => {
    if (!url || printing) return;

    if (window.ReactNativeWebView) {
      setPrinting(true);
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: "PRINT_FILE", url, filename: selectedFileTitle }));
      setTimeout(() => setPrinting(false), 1500);
      return;
    }

    setPrinting(true);

    const cleanUrl = url.split("?")[0].toLowerCase();
    const isPdf = cleanUrl.endsWith(".pdf");

    const oldIframe = document.getElementById("timetablePrintIframe");
    if (oldIframe) oldIframe.remove();

    const iframe = document.createElement("iframe");
    iframe.id = "timetablePrintIframe";
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.style.zIndex = "-9999";
    document.body.appendChild(iframe);

    try {
      let imageHtml = "";
      if (isPdf) {
        try {
          const pageImages = await renderPdfToImages(url);
          imageHtml = pageImages.map(src => `<img src="${src}" class="pdfPrintPage" />`).join("");
        } catch (pdfErr) {
          console.warn("PDF render for print failed:", pdfErr);
          imageHtml = `<iframe src="${url}" style="width:100%;height:100vh;border:none;"></iframe>`;
        }
      } else {
        let printSrc = url;
        try {
          const res = await fetch(url);
          if (res.ok) {
            const blob = await res.blob();
            printSrc = window.URL.createObjectURL(blob);
          }
        } catch (e) {
          console.warn("Fetch blob failed for print image:", e);
        }
        imageHtml = `<img src="${printSrc}" class="pdfPrintPage" />`;
      }

      const docHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Timetable</title>
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <style>
              body { margin: 0; padding: 0; background: #ffffff; text-align: center; }
              .pdfPrintPage { max-width: 100%; height: auto; display: block; margin: 0 auto 10px auto; page-break-after: always; }
              .pdfPrintPage:last-child { page-break-after: auto; }
              @media print {
                body { margin: 0; padding: 0; }
                .pdfPrintPage { max-width: 100%; width: 100%; height: auto; page-break-after: always; }
                .pdfPrintPage:last-child { page-break-after: auto; }
              }
            </style>
          </head>
          <body>
            ${imageHtml}
          </body>
        </html>
      `;

      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(docHtml);
      iframeDoc.close();

      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error("Iframe print error:", e);
        }
        setPrinting(false);
        setTimeout(() => iframe.remove(), 2000);
      }, 500);
    } catch (err) {
      console.error("Print error:", err);
      iframe.remove();
      setPrinting(false);
    }
  };


  const downloadFile = async (url, filename) => {
    if (!url) return;
    setDownloading(true);
    try {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: "DOWNLOAD_FILE", url, filename }));
        setTimeout(() => setDownloading(false), 1500);
        return;
      }

      const cleanUrl = url.split("?")[0].toLowerCase();
      let ext = ".jpg";
      if (cleanUrl.endsWith(".pdf")) ext = ".pdf";
      else if (cleanUrl.endsWith(".png")) ext = ".png";
      else if (cleanUrl.endsWith(".jpg") || cleanUrl.endsWith(".jpeg")) ext = ".jpg";
      else if (cleanUrl.endsWith(".gif")) ext = ".gif";
      else if (cleanUrl.endsWith(".webp")) ext = ".webp";
      else if (cleanUrl.endsWith(".docx")) ext = ".docx";
      else if (cleanUrl.endsWith(".xlsx")) ext = ".xlsx";

      const name = (filename || "timetable-file").replace(/[^a-zA-Z0-9-_]/g, "_") + ext;

      // Method 1: Fetch as blob
      try {
        const response = await fetch(url);
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = name;
          document.body.appendChild(link);
          link.click();
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          }, 1000);
          setDownloading(false);
          return;
        }
      } catch (fetchErr) {
        console.warn("Fetch blob download failed, trying canvas/direct download...", fetchErr);
      }

      // Method 2: Canvas DataURL download for images
      const isImage = [".png", ".jpg", ".jpeg", ".gif", ".webp"].some(e => cleanUrl.endsWith(e));
      if (isImage) {
        try {
          const dataUrl = await new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
              try {
                const canvas = document.createElement("canvas");
                canvas.width = img.naturalWidth || img.width;
                canvas.height = img.naturalHeight || img.height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL(ext === ".png" ? "image/png" : "image/jpeg", 0.95));
              } catch (e) { resolve(null); }
            };
            img.onerror = () => resolve(null);
            img.src = url;
          });

          if (dataUrl) {
            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = name;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => document.body.removeChild(link), 1000);
            setDownloading(false);
            return;
          }
        } catch (canvasErr) {
          console.warn("Canvas download failed", canvasErr);
        }
      }

      // Method 3: Direct link click
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 1000);

    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setDownloading(false);
    }
  };

  const getFileType = (url) => {
    if (!url) return "";
    try {
      const cleanUrl = url.split("?")[0].toLowerCase();
      if (cleanUrl.endsWith(".pdf")) return "pdf";

      const docExtensions = [".docx", ".doc", ".xlsx", ".xls", ".pptx", ".ppt", ".zip", ".rar", ".txt", ".csv"];
      if (docExtensions.some(ext => cleanUrl.endsWith(ext))) {
        return "other";
      }

      return "image";
    } catch (e) {
      console.error(e);
    }
    return "image";
  };

  useEffect(() => {
    const fetchTimetableImage = async () => {
      try {
        setLoadingImage(true);

        const authtoken = localStorage.getItem('token');

        const response = await fetch(
          `https://api.mirai.healthcare/api/timetable/timetable-upload/filter?session_id=${sessionId}&cls_sec_id=${clsSecId}`,
          //   `https://api.mirai.healthcare/api/timetable/timetable-upload/filter?session_id=2&cls_sec_id=12`,
          {
            method: "GET",
            headers: { "x-auth": `${authtoken}` }
          }
        );

        const data = await response.json();
        console.log(data, 's3url 1500')


        if (data?.message?.length > 0) {
          setTimetableImage(data?.message[0]);
          setShowImage(true);

        } else {
          console.log('');
        }

      } catch (err) {
        console.log(err);
      } finally {
        setLoadingImage(false);
      }
    };
    fetchTimetableImage();
  }, [])

  // Fetch timetable data for selected season
  const fetchTimetableData = async (seasonId) => {
    if (!clsSecId || !seasonId) return;
    const cacheKey = `timetable_data_${clsSecId}_${seasonId}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      setTimetableData(cached);
      setViewMode('season-details');
      setLoading(false);

    }
    try {
      if (!cached) setLoading(true);
      const authtoken = localStorage.getItem('token');
      const response = await fetch(
        `https://api.mirai.healthcare/api/timetable/time-table?cls_sec_id=${clsSecId}&schl_time_id=${seasonId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'x-auth': authtoken
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.message && Array.isArray(data.message) && data.message.length > 0) {
        setTimetableData(data);
        setCachedData(cacheKey, data);
        setViewMode('season-details');
      } else {
        setTimetableData(null);
        Swal.fire({
          icon: 'info',
          title: 'No Timetable Found',
          text: 'No timetable data available for the selected season.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#17a2b8'
        });
      }
    } catch (err) {
      console.error('Error fetching timetable data:', err);
      setError(err.message || 'Failed to fetch timetable data');
    } finally {
      setLoading(false);
    }
  };


  // Fetch optional teachers for period
  const fetchOptionalTeachers = async (timeTableId) => {
    setOptionalTeachers([]);

    if (!timeTableId) {
      return;
    }

    try {
      const authtoken = localStorage.getItem('token');
      const response = await fetch(
        `https://api.mirai.healthcare/api/timetable/time-table/optional?time_table_id=${timeTableId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'x-auth': authtoken
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.message && Array.isArray(data.message)) {
        setOptionalTeachers(data.message);
      }
    } catch (err) {
      console.error('Error fetching optional teachers:', err);
    }
  };

  useEffect(() => {
    fetchSeasons();
  }, [clsSecId, sessionId]);

  useEffect(() => {
    if (selectedSeasonId) {
      fetchTimetableData(selectedSeasonId);
    }
  }, [selectedSeasonId]);

  const handleSeasonChange = (selectedOption) => {
    setSelectedSeasonId(selectedOption ? selectedOption.value : null);
  };

  const handleViewSchedule = () => {
    if (selectedSeasonId) {
      if (timetableData && timetableData.message && timetableData.message.length > 0) {
        setViewMode('days');
      } else {
        Swal.fire({
          icon: 'info',
          title: 'No Timetable Available',
          text: 'No timetable data found for the selected season.',
          confirmButtonText: 'OK'
        });
      }
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Select Season',
        text: 'Please select a season to view the schedule.',
      });
    }
  };

  const handleDayClick = (dayName) => {
    setSelectedDay(dayName);
    setSelectedPeriod(null);
    setSelectedTimeTableId(null);
    setSelectedTimeTableEntry(null);
    setOptionalTeachers([]);
    setViewMode('periods');
  };

  // const handlePeriodClick = (dayName, periodName, timeTableEntry) => {
  //     const entryId = timeTableEntry?.id || null;
  //     const resolvedDay = dayName || selectedDay || timeTableEntry?.day?.name || null;

  //     if (resolvedDay) {
  //         setSelectedDay(resolvedDay);
  //     }
  //     setSelectedPeriod(periodName);
  //     setSelectedTimeTableId(entryId);
  //     setSelectedTimeTableEntry(timeTableEntry || null);

  //     if (entryId) {
  //         fetchOptionalTeachers(entryId);
  //     } else {
  //         setOptionalTeachers([]);
  //     }

  //     setViewMode('details');
  // };

  const handlePeriodClick = (day, periodName, tt, periodData) => {
    setSelectedDay(day);
    setSelectedPeriod(periodName);
    setSelectedTimeTableEntry(tt);

    setSelectedData({ day, periodName, tt, periodData });
    setShowModal(true); // 👈 modal open
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleBack = () => {
    if (viewMode === 'details') {
      setViewMode('periods');
      setSelectedPeriod(null);
      setSelectedTimeTableId(null);
      setSelectedTimeTableEntry(null);
      setOptionalTeachers([]);
    } else if (viewMode === 'periods') {
      setViewMode('days');
      setSelectedDay(null);
      setSelectedPeriod(null);
      setSelectedTimeTableId(null);
      setSelectedTimeTableEntry(null);
      setOptionalTeachers([]);
    } else if (viewMode === 'days') {
      setViewMode('season-details');
      setSelectedDay(null);
      setSelectedPeriod(null);
      setSelectedTimeTableId(null);
      setSelectedTimeTableEntry(null);
      setOptionalTeachers([]);
    } else if (viewMode === 'season-details') {
      setViewMode('seasons');
      setSelectedSeasonId(null);
      setTimetableData(null);
      setSelectedDay(null);
      setSelectedPeriod(null);
      setSelectedTimeTableId(null);
      setSelectedTimeTableEntry(null);
      setOptionalTeachers([]);
    }
  };

  // Print Function
  const handlePrint = () => {
    Swal.fire({
      icon: 'success',
      title: 'Print Started',
      text: 'Timetable is being prepared for printing.',
      confirmButtonText: 'OK',
      confirmButtonColor: '#28a745',
      timer: 2000,
      showConfirmButton: false
    });

    setTimeout(() => {
      window.print();
    }, 500);
  };

  // Download Function
  const handleDownload = () => {
    Swal.fire({
      icon: 'success',
      title: 'Download Started',
      text: 'Timetable download has been initiated.',
      confirmButtonText: 'OK',
      confirmButtonColor: '#17a2b8',
      timer: 2000,
      showConfirmButton: false
    });
  };

  // Refresh Function
  const handleRefresh = () => {
    setRefreshing(true);
    fetchSeasons();

    setTimeout(() => {
      Swal.fire({
        icon: 'success',
        title: 'Refreshed',
        text: 'Timetable has been refreshed successfully.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#17a2b8',
        timer: 2000,
        showConfirmButton: false
      });
      setRefreshing(false);
    }, 500);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return moment(dateString).format('DD MMMM YYYY');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const formatTime = (dateString) => {
    return moment(dateString).format('hh:mm A');
  };

  const getStatusBadge = (startDate, endDate) => {
    if (!startDate || !endDate) {
      return <Badge bg="secondary">N/A</Badge>;
    }

    try {
      const today = moment().startOf('day');
      const start = moment(startDate).startOf('day');
      const end = moment(endDate).startOf('day');

      if (today.isAfter(end)) {
        return <Badge bg="secondary">Completed</Badge>;
      } else if (today.isBetween(start, end)) {
        return <Badge bg="success">Active</Badge>;
      } else {
        return <Badge bg="primary">Upcoming</Badge>;
      }
    } catch (error) {
      console.error('Error calculating status:', error);
      return <Badge bg="secondary">N/A</Badge>;
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'primary' }) => (
    <Card className="stat-card border-0 h-100 shadow-sm">
      <Card.Body className="p-3">
        <div className="d-flex align-items-center">
          <div className={`icon-container bg-${color} me-3 flex-shrink-0`}>
            <Icon size={20} className="text-white" />
          </div>
          <div className="flex-grow-1">
            <h6 className="text-muted mb-1 small">{title}</h6>
            <h4 className="fw-bold text-dark mb-0">{value}</h4>
            {subtitle && <small className="text-muted">{subtitle}</small>}
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Header />
          <Container className="py-5">
            <div className="text-center py-5">
              <div className="loading-spinner">
                <FaCalendarAlt size={48} className="text-primary mb-3" />
                <Spinner animation="border" variant="primary" className="mb-3" />
                <h5 className="text-dark">Loading Timetable</h5>
                <p className="text-muted">Fetching schedule...</p>
              </div>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Header />
          <Container className="py-5">
            <div className="error-state text-center">
              <FaExclamationTriangle size={64} className="text-danger mb-3 opacity-50" />
              <h4 className="text-dark mb-3">Unable to Load Timetable</h4>
              <p className="text-muted mb-4">{error}</p>
              <div className="d-flex gap-2 justify-content-center">
                <Button variant="primary" onClick={fetchSeasons}>
                  <FaSync className="me-2" />
                  Try Again
                </Button>
                <Button variant="outline-secondary" onClick={() => window.history.back()}>
                  <FaArrowLeft className="me-2" />
                  Go Back
                </Button>
              </div>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  const seasonDetails = timetableData?.message?.[0]?.time_table?.[0]?.schl_time || {};
  const allTimeTable = timetableData?.message?.flatMap(item => item?.time_table || []) || [];
  const uniqueDayMap = new Map();
  allTimeTable.forEach(tt => {
    const dayName = tt?.day?.name;
    if (dayName && !uniqueDayMap.has(dayName)) {
      uniqueDayMap.set(dayName, tt);
    }
  });

  const weekdayOrderMap = {
    Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7,
  };
  const sortedUniqueDays = [...uniqueDayMap.entries()].sort(([dayA], [dayB]) => (weekdayOrderMap[dayA] || 99) - (weekdayOrderMap[dayB] || 99));

  const filteredPeriodsForDay = (dayName) => {
    return timetableData?.message?.filter(item =>
      item?.time_table?.some(tt => tt?.day?.name === dayName)
    ) || [];
  };

  const periodDetailsForDayAndPeriod = (dayName, periodName) => {
    return timetableData?.message?.find(item =>
      item?.periodName === periodName &&
      item?.time_table?.some(tt => tt?.day?.name === dayName)
    ) || null;
  };

  console.log(selectedData, 'selectedData data');

  return (
    <div className="page-wrapper">
      <div className="content">
        <Header />

        <div className="py-4">
          {/* Modern Header */}
          <div className="bg-light border-bottom py-3 mb-4">
            <div className="container-fluid">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-between gap-3">

                {/* Title */}
                <div className="d-flex align-items-center gap-2">
                  <FaCalendarAlt className="text-primary fs-4" />
                  <div>
                    <h2
                      className="page-title fw-bold mb-0"
                      style={{ letterSpacing: "0.5px" }}>School Timetable</h2>
                    <small className="text-muted">
                      Class: {className} - {sectionName}
                    </small>
                  </div>
                </div>

              </div>
            </div>
          </div>


          {/* Stats Cards */}
          <div className="stat-scroll-container mb-4">
            <div className="stat-scroll d-flex gap-3">
              <div style={{ minWidth: "230px" }}>
                <StatCard
                  icon={FaCalendarAlt}
                  title="Total Seasons"
                  value={seasonOptions.length}
                  subtitle="Available schedules"
                  color="primary"
                />
              </div>

              <div style={{ minWidth: "230px" }}>
                <StatCard
                  icon={FaListAlt}
                  title="Total Days"
                  value={timetableData ? sortedUniqueDays.length : 0}
                  subtitle="Weekly structure"
                  color="info"
                />
              </div>

              <div style={{ minWidth: "230px" }}>
                <StatCard
                  icon={FaClock}
                  title="Active Periods"
                  value={timetableData?.message?.length || 0}
                  subtitle="Daily slots"
                  color="success"
                />
              </div>

              <div style={{ minWidth: "230px" }}>
                <StatCard
                  icon={FaUserGraduate}
                  title="Teachers Assigned"
                  value={timetableData ? new Set(allTimeTable.map(tt => tt?.teacher?.id).filter(Boolean)).size : 0}
                  subtitle="Unique faculty"
                  color="warning"
                />
              </div>
            </div>
          </div>


          {/* Season Selection */}
          {viewMode === 'seasons' && (
            <Card className="border-0 shadow-sm mb-4 rounded-3">
              <Card.Header className="bg-light border-0 py-3">
                <h6 className="mb-0 fw-semibold d-flex align-items-center">
                  <FaCalendarCheck className="me-2 text-primary fs-5" />
                  Select Season
                </h6>
              </Card.Header>

              <Card.Body className="pt-2">
                <Row className="g-3">
                  <Col md={6}>
                    <Select
                      options={seasonOptions}
                      value={seasonOptions.find(opt => opt.value === selectedSeasonId)}
                      onChange={handleSeasonChange}
                      placeholder="Choose a season..."
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isClearable
                      isSearchable
                    />
                  </Col>

                  <Col md={6} className="d-flex align-items-center">
                    <Button
                      variant="primary"
                      className="w-100 w-md-auto"
                      onClick={handleViewSchedule}
                      disabled={!selectedSeasonId}
                    >
                      View Schedule
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}


          {/* Season Details */}
          {selectedSeasonId && timetableData && (
            <Accordion defaultActiveKey="0" className="mb-4">

              {/* 1. Season Details */}
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <FaSchool className="me-2 text-primary" />
                  Season Details
                </Accordion.Header>
                <Accordion.Body>
                  {(() => {
                    const selectedSeason = seasonOptions.find(
                      (season) => season.value === selectedSeasonId
                    );
                    const seasonData = selectedSeason?.seasonData;

                    if (!selectedSeason) {
                      return <Alert variant="warning">Season not found</Alert>;
                    }

                    return (
                      <Row>
                        <Col md={6}>
                          <p><b>Season:</b> {selectedSeason.label}</p>
                          <p><b>Start Time:</b> {seasonData?.shift_start_time}</p>
                          <p><b>End Time:</b> {seasonData?.shift_end_time}</p>
                          <p><b>Start Date:</b> {formatDate(seasonData?.start_date)}</p>
                          <p><b>End Date:</b> {formatDate(seasonData?.end_date)}</p>
                          <p><b>Status:</b> {getStatusBadge(seasonData?.start_date, seasonData?.end_date)}</p>
                        </Col>

                        <Col md={6}>
                          <p><b>Description:</b> {seasonData?.description}</p>
                          <p><b>Shift:</b> {seasonData?.shift}</p>
                        </Col>
                      </Row>
                    );
                  })()}
                </Accordion.Body>
              </Accordion.Item>

              {/* 2. Weekly Schedule */}
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <FaListAlt className="me-2 text-success" />
                  Weekly Schedule
                </Accordion.Header>
                <Accordion.Body>
                  <Card className="modern-card shadow-sm mb-4">
                    {/* <Card.Header className="modern-card-header d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <FaListAlt className="me-2 text-primary" />
                                    Weekly Schedule
                                </h5>
                                <Button variant="outline-secondary" size="sm" onClick={handleBack}>
                                    <FaArrowLeft className="me-1" /> Back to Season Details
                                </Button>
                            </Card.Header> */}
                    <Card.Body>
                      <Row className="g-3 weekly-schedule-row">
                        {sortedUniqueDays.map(([dayName, tt]) => (
                          <Col lg={6} xl={4} className="weekly-schedule-col" key={dayName}>
                            <Card className="h-100 border-0 shadow-sm timetable-day-card">
                              <Card.Header className="bg-light border-bottom">
                                <div className="d-flex justify-content-between align-items-center">
                                  <h6 className="mb-0 fw-bold">{dayName}</h6>
                                  <Badge bg="info">{tt?.day?.id || 'N/A'}</Badge>
                                </div>
                              </Card.Header>
                              <Card.Body className="p-0">
                                <Accordion defaultActiveKey="0">
                                  {filteredPeriodsForDay(dayName).map((period, idx) => (
                                    <Accordion.Item eventKey={idx.toString()} key={idx}>
                                      <Accordion.Header>
                                        <div className="d-flex align-items-center w-100 justify-content-between">
                                          <div className="d-flex align-items-center">
                                            <FaBook className="me-2" />
                                            {period.periodName} ({(period.startTime)} - {(period.closeTime)})
                                          </div>
                                        </div>
                                      </Accordion.Header>
                                      <Accordion.Body className="p-0">
                                        <Table size="sm" className="mb-0">
                                          <thead className="table-light">
                                            <tr>
                                              <th>Subject</th>
                                              <th>Teacher</th>
                                              <th>Action</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {period.time_table?.filter(ttt => ttt.day?.name === dayName).map((ttt, ttIdx) => (
                                              <tr key={ttIdx}>
                                                <td>
                                                  <strong>{ttt.paper?.name || 'N/A'}</strong>
                                                  <small className="text-muted d-block">
                                                    {ttt.paper?.chapter_topic || 'All Chapters'}
                                                  </small>
                                                </td>
                                                <td>
                                                  {ttt.teacher ? (
                                                    <span className="fw-bold">{ttt.teacher.first_name} {ttt.teacher.last_name}</span>
                                                  ) : (
                                                    <span className="text-muted">No Teacher Assigned</span>
                                                  )}
                                                </td>
                                                <td>
                                                  <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => handlePeriodClick(dayName, period.periodName, ttt)}
                                                  >
                                                    View Details
                                                  </Button>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </Table>
                                      </Accordion.Body>
                                    </Accordion.Item>
                                  ))}
                                </Accordion>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>




                  <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
                    <Modal.Header closeButton>
                      <Modal.Title>
                        Period Details: {selectedPeriod} ({selectedDay})
                      </Modal.Title>
                    </Modal.Header>


                    <Modal.Body>
                      <Card className="modern-card shadow-sm mb-4">

                        <Card.Body>
                          {(() => {
                            const periodData = periodDetailsForDayAndPeriod(selectedDay, selectedPeriod);
                            if (!periodData) return <Alert variant="warning">No details available.</Alert>;

                            const tt = selectedTimeTableEntry ||
                              allTimeTable.find(t => t.id === selectedTimeTableId) ||
                              periodData.time_table?.find(t => t.day?.name === selectedDay) ||
                              {};
                            return (
                              <Row className="g-3">
                                <Col md={6}>
                                  <div className="border rounded p-3">
                                    <h6 className="text-muted mb-3">Basic Info</h6>
                                    <div className="d-flex justify-content-between mb-2">
                                      <span className="text-muted">Day:</span>
                                      <span>{selectedDay}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                      <span className="text-muted">Period:</span>
                                      <span>{selectedPeriod}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                      <span className="text-muted">Start Time:</span>
                                      <span>{(periodData.startTime)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                      <span className="text-muted">End Time:</span>
                                      <span>{(periodData.closeTime)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                      <span className="text-muted">Subject:</span>
                                      <span>{tt.paper?.name || 'N/A'}</span>
                                    </div>
                                  </div>
                                </Col>
                                <Col md={6}>
                                  {/* Teacher Info */}
                                  <div className="border rounded p-3">
                                    <h6 className="text-muted mb-3">Teacher Info</h6>

                                    {tt.teacher ? (
                                      <div className="d-flex gap-3 align-items-start">

                                        {/* Teacher Photo */}
                                        <div>
                                          <img
                                            src={`https://api.mirai.healthcare/${tt.teacher.photo}`}
                                            alt="Teacher"
                                            style={{
                                              width: "70px",
                                              height: "70px",
                                              borderRadius: "8px",
                                              objectFit: "cover",
                                              border: "1px solid #ddd"
                                            }}
                                          />
                                        </div>

                                        {/* Teacher Details */}
                                        <div className="flex-grow-1">

                                          <div className="mb-1">
                                            <span className="text-muted">Name:</span>
                                            <span className="fw-bold ms-2">
                                              {tt.teacher.first_name} {tt.teacher.last_name}
                                            </span>
                                          </div>

                                          <div className="mb-1">
                                            <span className="text-muted">Email:</span>
                                            <span className="ms-2">{tt.teacher.email || 'N/A'}</span>
                                          </div>

                                          <div className="mb-1">
                                            <span className="text-muted">Mobile:</span>
                                            <span className="ms-2">{tt.teacher.mobile_no || 'N/A'}</span>
                                          </div>

                                          <div className="mb-1">
                                            <span className="text-muted">Position:</span>
                                            <span className="ms-2">{tt.teacher.position || 'Teacher'}</span>
                                          </div>

                                          <div className="mb-1">
                                            <span className="text-muted">Department:</span>
                                            <span className="ms-2">{tt.teacher.department || 'N/A'}</span>
                                          </div>

                                        </div>

                                      </div>
                                    ) : (
                                      <Alert variant="info" className="mb-2">No Main Teacher Assigned</Alert>
                                    )}

                                    {/* Optional Teachers */}
                                    {optionalTeachers.length > 0 && (
                                      <div className="mt-3">
                                        <span className="text-muted d-block mb-2">Optional Teachers:</span>
                                        <ul className="mb-0 small">
                                          {optionalTeachers.map((opt, idx) => (
                                            <li key={idx}>
                                              {opt.teacher?.first_name} {opt.teacher?.last_name}
                                              {opt.teacher?.email && ` (${opt.teacher.email})`}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>

                                </Col>
                              </Row>
                            );
                          })()}
                        </Card.Body>
                      </Card>
                    </Modal.Body>

                    <Modal.Footer>
                      <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                      </Button>
                    </Modal.Footer>
                  </Modal>




                </Accordion.Body>
              </Accordion.Item>

              {/* 3. Time Table */}
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  <FaClock className="me-2 text-info" />
                  Time Table
                </Accordion.Header>
                <Accordion.Body>
                  {timetableImage && (
                    <div className="mb-3">
                      <Button
                        variant="primary"
                        className="d-inline-flex align-items-center gap-2 mt-3"
                        onClick={() => handleOpenFileModal(timetableImage?.s3url, "School Timetable")}
                      >
                        <i className="ti ti-printer fs-14" /> View / Print Time Table
                      </Button>
                    </div>
                  )}

                  {loadingImage ? (
                    <Spinner />
                  ) : timetableImage ? (
                    <div
                      onClick={() => handleOpenFileModal(timetableImage?.s3url, "School Timetable")}
                      style={{ cursor: "pointer", display: "inline-block", width: "100%" }}
                      title="Click to view and print timetable"
                    >
                      <img
                        src={timetableImage?.s3url}
                        alt="Timetable"
                        style={{
                          width: "100%",
                          maxHeight: "500px",
                          objectFit: "contain",
                          borderRadius: "10px",
                          transition: "opacity 0.2s ease"
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1.0'; }}
                      />
                    </div>
                  ) : (
                    <p>No Image Found</p>
                  )}

                </Accordion.Body>
              </Accordion.Item>

              {/* 4. Schedule Summary */}
              <Accordion.Item eventKey="3">
                <Accordion.Header>
                  <FaChartBar className="me-2 text-warning" />
                  Schedule Summary
                </Accordion.Header>
                <Accordion.Body>

                  {/* <p><b>Total Periods:</b> {timetableData?.message?.length}</p>

        <p>
          <b>Subjects:</b>{" "}
          {new Set(allTimeTable.map(tt => tt.paper?.name).filter(Boolean)).size}
        </p>

        <p>
          <b>Teachers:</b>{" "}
          {new Set(allTimeTable.map(tt => tt.teacher?.id).filter(Boolean)).size}
        </p> */}

                  {viewMode !== 'details' && timetableData && (

                    <>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">Total Periods:</span>
                        <Badge bg="info">{timetableData?.message?.length || 0}</Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">Assigned Subjects:</span>
                        <Badge bg="success">{new Set(allTimeTable.map(tt => tt.paper?.name).filter(Boolean)).size}</Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Teachers:</span>
                        <Badge bg="warning" text="dark">{new Set(allTimeTable.map(tt => tt.teacher?.id).filter(Boolean)).size}</Badge>
                      </div>
                    </>

                  )}

                </Accordion.Body>
              </Accordion.Item>

            </Accordion>
          )}





          {/* Quick Actions if not in details */}

        </div>



        <style jsx>{`
  .page-header-simple {
    background: #ffffff;
    border: 1px solid #e9ecef;
    padding: 1.5rem;
    border-radius: 8px;
    margin-bottom: 2rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .simple-breadcrumb {
    background: transparent;
    padding: 0;
    margin-bottom: 0;
  }

  .simple-breadcrumb .breadcrumb-item a {
    color: #6c757d !important;
    font-size: 0.875rem;
    text-decoration: none;
  }

  .simple-breadcrumb .breadcrumb-item.active {
    color: #495057 !important;
    font-weight: 500;
  }

  .simple-breadcrumb .breadcrumb-item + .breadcrumb-item::before {
    color: #6c757d;
    content: "/";
    padding: 0 0.5rem;
  }

  .header-icon-simple {
    background: #f8f9fa;
    padding: 0.75rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e9ecef;
  }

  .page-title-simple {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
    color: #212529;
    line-height: 1.2;
  }

  .action-buttons {
    flex-shrink: 0;
  }

  .btn-action {
    border-radius: 6px;
    padding: 0.5rem 1rem;
    font-weight: 500;
    font-size: 0.875rem;
    border: 1px solid;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .btn-action:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .stat-card {
    border-radius: 8px;
    transition: all 0.3s ease;
  }

  .stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
  }

  .icon-container {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modern-card {
    border-radius: 8px;
    overflow: hidden;
  }

  .modern-card-header {
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    padding: 1rem 1.5rem;
  }

  .exam-type-header {
    background: #f8f9fa;
    border: none;
    padding: 1rem 1.5rem;
  }

  .exam-type-header:not(.collapsed) {
    background: #e3f2fd;
  }

  .accordion-button:not(.collapsed) {
    background: #e3f2fd;
    color: #212529;
    box-shadow: none;
  }

  .accordion-button:focus {
    box-shadow: none;
    border-color: rgba(0,0,0,.125);
  }

  .table th {
    border-top: none;
    font-weight: 600;
    font-size: 0.875rem;
    text-transform: uppercase;
    color: #6c757d;
  }

  .table td {
    vertical-align: middle;
    padding: 1rem 0.75rem;
  }

  .timetable-day-card {
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .timetable-day-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
  }

  .loading-spinner {
    max-width: 400px;
    margin: 0 auto;
  }

  .error-state {
    max-width: 500px;
    margin: 0 auto;
  }

  .rotate {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .weekly-schedule-row {
    @media (max-width: 768px) {
      flex-direction: column;
      flex-wrap: nowrap !important;
      gap: 1rem;
      overflow: visible !important;
      padding-bottom: 0;
    }
  }

  .weekly-schedule-col {
    @media (max-width: 768px) {
      flex: 1 0 100% !important;
      max-width: 100% !important;
    }
  }

  @media (max-width: 768px) {
    .page-header-simple {
      padding: 1.25rem;
      margin-bottom: 1.5rem;
    }

    .page-title-simple {
      font-size: 1.25rem;
    }

    .d-flex.flex-column.flex-md-row {
      gap: 1rem !important;
    }

    .action-buttons {
      width: 100%;
      justify-content: center;
    }

    .btn-action {
      flex: 1;
      min-width: 0;
      padding: 0.5rem 0.75rem;
    }

    .table-responsive {
      font-size: 0.875rem;
    }

    .table th,
    .table td {
      padding: 0.5rem 0.25rem;
    }
  }

  @media (max-width: 576px) {
    .page-header-simple {
      padding: 1rem;
    }

    .page-title-simple {
      font-size: 1.125rem;
    }

    .action-buttons {
      flex-wrap: wrap;
    }

    .btn-action {
      flex: 0 0 calc(50% - 0.25rem);
      margin-bottom: 0.5rem;
    }

    .d-flex.align-items-center.gap-3 {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem !important;
    }

    .header-icon-simple {
      align-self: flex-start;
    }

    .weekly-schedule-col {
      @media (max-width: 768px) {
        flex: 1 0 100% !important;
        max-width: 100% !important;
      }
    }
  }

  @media print {
    .action-buttons,
    .btn-action,
    .modern-card-header .btn {
      display: none !important;
    }
    
    .page-header-simple {
      box-shadow: none !important;
      border: 1px solid #000 !important;
    }
    
    .stat-card {
      break-inside: avoid;
    }
  }
    .stat-scroll-container {
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding-bottom: 5px;
}

.stat-scroll {
  display: inline-flex;
  white-space: nowrap;
}

.stat-scroll::-webkit-scrollbar {
  height: 6px;
}

.stat-scroll::-webkit-scrollbar-thumb {
  background: #cfcfcf;
  border-radius: 10px;
}

`}</style>
        {/* Timetable File Preview Modal */}
        <Modal
          show={showFileModal}
          onHide={() => setShowFileModal(false)}
          size="lg"
          centered
          className="timetable-file-modal"
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold fs-16 text-dark">
              {selectedFileTitle || "Timetable Document"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3">
            {getFileType(selectedFileUrl) === "image" && (
              <div className="d-flex justify-content-center gap-2 mb-3 bg-light p-2 rounded border border-light">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setZoomScale(prev => Math.max(0.2, prev - 0.1))}
                  title="Zoom Out"
                  className="d-flex align-items-center justify-content-center"
                  style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                >
                  <i className="ti ti-zoom-out fs-14" />
                </Button>
                <span className="d-flex align-items-center justify-content-center fw-semibold text-secondary px-2" style={{ fontSize: "13px", minWidth: "55px" }}>
                  {Math.round(zoomScale * 100)}%
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setZoomScale(prev => Math.min(3, prev + 0.1))}
                  title="Zoom In"
                  className="d-flex align-items-center justify-content-center"
                  style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                >
                  <i className="ti ti-zoom-in fs-14" />
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setZoomScale(1)}
                  title="Reset Zoom"
                  className="d-flex align-items-center justify-content-center ms-2"
                  style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                >
                  <i className="ti ti-refresh fs-14" />
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setRotationDegrees(prev => (prev + 90) % 360)}
                  title="Rotate"
                  className="d-flex align-items-center justify-content-center ms-1"
                  style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                >
                  <i className="ti ti-rotate-clockwise fs-14" />
                </Button>
              </div>
            )}
            <div
              className="d-flex align-items-center justify-content-center bg-light rounded position-relative"
              style={{
                minHeight: "450px",
                height: "60vh",
                overflow: "auto",
                border: "1px dashed #cbd5e1"
              }}
            >
              {getFileType(selectedFileUrl) === "pdf" ? (
                <div className="position-relative w-100 h-100 overflow-hidden">
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: "48px",
                      height: "48px",
                      backgroundColor: "#ffffff",
                      zIndex: 10,
                      pointerEvents: "auto"
                    }}
                  />
                  <iframe
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(selectedFileUrl)}&embedded=true`}
                    className="w-100 h-100 rounded"
                    style={{ border: "none" }}
                    title="Timetable PDF Preview"
                  />
                </div>
              ) : getFileType(selectedFileUrl) === "image" ? (


                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    minWidth: "100%",
                    minHeight: "100%",
                    padding: "20px",
                    overflow: "auto"
                  }}
                >
                  <img
                    src={selectedFileUrl}
                    alt="Timetable Preview"
                    className="img-fluid rounded"
                    style={{
                      maxHeight: "100%",
                      transform: `scale(${zoomScale}) rotate(${rotationDegrees}deg)`,
                      transformOrigin: "center center",
                      transition: "transform 0.2s ease-in-out",
                      objectFit: "contain"
                    }}
                  />
                </div>
              ) : (
                <div className="text-center p-4">
                  <i className="ti ti-file-text text-muted mb-3" style={{ fontSize: "64px" }} />
                  <p className="text-secondary fw-medium mb-1">Preview not available for this file type</p>
                  <p className="text-muted small mb-0">Please print or open the file using the button below to view it.</p>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button
              variant="light"
              onClick={() => setShowFileModal(false)}
              style={{ borderRadius: "8px", fontWeight: "600" }}
            >
              Close
            </Button>
            {getFileType(selectedFileUrl) === "pdf" ? (
              <Button
                variant="primary"
                disabled={downloading}
                onClick={() => downloadFile(selectedFileUrl, selectedFileTitle)}
                className="d-flex align-items-center gap-2 px-4"
                style={{ borderRadius: "8px", fontWeight: "600" }}
              >
                {downloading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" /> Downloading...
                  </>
                ) : (
                  <>
                    <i className="ti ti-download" /> Download File
                  </>
                )}
              </Button>
            ) : (
              <Button
                variant="primary"
                disabled={printing}
                onClick={() => printFile(selectedFileUrl)}
                className="d-flex align-items-center gap-2 px-4"
                style={{ borderRadius: "8px", fontWeight: "600" }}
              >
                {printing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" /> Preparing...
                  </>
                ) : (
                  <>
                    <i className="ti ti-printer" /> Print
                  </>
                )}
              </Button>
            )}
          </Modal.Footer>


        </Modal>
      </div>
    </div>
  );
};

export default Timetable;
